import React, { useEffect, useState, useContext } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faTrash, faPenToSquare, faDollar, faCirclePlus, faStore } from '@fortawesome/free-solid-svg-icons'
import { Formik, Form, Field } from 'formik'
import CustomLoader from '../layouts/CustomLoader'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import AuthContext from '../../middlewares/AuthContext'
import { useTranslation } from 'react-i18next'
import usePaginationComponentOptions from '../layouts/paginationComponentOptions'
import { DateTime } from 'luxon'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface StorePayment {
  cs_checkout_id : string | null
  pi_stripe_id   : string | null
  payment_status : string
  price          : number
  email          : string
  name           : string
  description    : string
  country        : string
  expireAt       : string | null
  expiry_time    : number
  Time?          : { name: string } | null
}

interface Store {
  id           : number
  name         : string
  email        : string
  phone        : string
  whatsapp     : string
  link         : string
  image        : string
  Storepayments: StorePayment[]
}

const pill = (bg: string): React.CSSProperties => ({
  display        : 'inline-flex',
  alignItems     : 'center',
  gap            : '0.3rem',
  padding        : '0.28rem 0.75rem',
  borderRadius   : 9999,
  backgroundColor: bg,
  color          : '#fff',
  fontWeight     : 700,
  fontSize       : '0.75rem',
  border         : 'none',
  cursor         : 'pointer',
  boxShadow      : `0 2px 6px ${bg}44`,
  textDecoration : 'none',
  whiteSpace     : 'nowrap' as const,
})

const Stores: React.FC = () => {
  const { getHeaders }              = useContext(AuthContext) as AuthCtx
  const { t, i18n }                 = useTranslation()
  const paginationComponentOptions  = usePaginationComponentOptions()

  const [data, setData]                                   = useState<Store[]>([])
  const [loading, setLoading]                             = useState(false)
  const [totalRows, setTotalRows]                         = useState(0)
  const [perPage, setPerPage]                             = useState(10)
  const [search, setSearch]                               = useState('')
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false)
  const [searchOpen, setSearchOpen]                       = useState(false)

  const fetchStores = async (
    page      : number,
    perPageVal = perPage,
    searchVal  = search
  ) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res = await axios.get<{ message: Store[]; total: number }>(
        `${url}stores?page=${page}&per_page=${perPageVal}&search=${searchVal}`,
        { headers }
      )
      setData(res.data.message)
      setTotalRows(res.data.total)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange    = (page: number) => fetchStores(page)
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    fetchStores(page, newPerPage)
  }
  const handleSearch = (values: { search: string }) => {
    setSearch(values.search)
    fetchStores(1, perPage, values.search)
  }

  const deleteStore = async (id: number) => {
    const result = await Swal.fire({
      title             : 'Are you sure?',
      text              : "You won't be able to revert this!",
      icon              : 'warning',
      showCancelButton  : true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor : '#d33',
      confirmButtonText : 'Yes, delete it!',
    })
    if (!result.isConfirmed) return
    try {
      const headers = await getHeaders()
      const res     = await axios.delete<{ message: Store[]; total: number; response: string }>(
        `${url}stores/${id}`, { headers }
      )
      setData(res.data.message)
      setTotalRows(res.data.total)
      setResetPaginationToggle(p => !p)
      Swal.fire({ icon: 'success', title: res.data.response })
    } catch (err: any) {
      Swal.fire({
        icon : 'error',
        title: err?.response?.data?.response || 'Error',
        text : err?.response?.data?.message,
      })
    }
  }

  const getUrlStore = async (checkoutId: string) => {
    Swal.fire({
      html             : `<div class="text-center"><div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div></div>`,
      showConfirmButton: false,
    })
    try {
      const headers = await getHeaders()
      const res     = await axios.get<{ message: string }>(`${url}stores/get-url/${checkoutId}`, { headers })
      Swal.fire({ html: `<code>${res.data.message}</code>` })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  useEffect(() => { fetchStores(1) }, [])

  // ── Expanded component ──────────────────────────────────────────────────────
  const ExpandedComponent: React.FC<{ data: Store }> = ({ data: store }) => {
    if (!store.Storepayments.length) {
      return (
        <div style={{ padding: '1rem', color: '#94a3b8', fontSize: '0.85rem' }}>
          No payments found.
        </div>
      )
    }
    return (
      <>
        {store.Storepayments.map((e, i) => {
          const expiry = e.expireAt
            ? DateTime.fromISO(e.expireAt, { zone: 'utc' }).setLocale(i18n.language).toFormat('yyyy LLLL dd')
            : '—'
          return (
            <div
              key={i}
              style={{
                display             : 'grid',
                gridTemplateColumns : '1fr 1fr',
                gap                 : '0',
                borderBottom        : '1px solid #f1f5f9',
                fontSize            : '0.82rem',
              }}
            >
              {/* Info */}
              <div style={{ padding: '0.75rem 1rem', borderRight: '1px solid #f1f5f9' }}>
                <p style={{ margin: '0 0 0.5rem', fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase' }}>
                  {t('stores.info')}
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li><b>Total:</b> ${e.price ?? '—'}</li>
                  <li><b>{t('stores.email')}:</b> {e.email || '—'}</li>
                  <li><b>{t('stores.name')}:</b> {e.name || '—'}</li>
                  <li><b>{t('stores.description')}:</b> {e.description || '—'}</li>
                  <li><b>{t('stores.expire_time')}:</b> {e.Time?.name ?? '—'} {e.expiry_time ?? '—'}</li>
                </ul>
              </div>
              {/* Payment info */}
              <div style={{ padding: '0.75rem 1rem' }}>
                <p style={{ margin: '0 0 0.5rem', fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase' }}>
                  {t('stores.info_pay')}
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li><b>Checkout:</b> {e.cs_checkout_id || '—'}</li>
                  <li><b>{t('stores.payment_id')}:</b> {e.pi_stripe_id || '—'}</li>
                  <li>
                    <b>{t('stores.status')}: </b>
                    <span style={{
                      display        : 'inline-block',
                      padding        : '1px 8px',
                      borderRadius   : 9999,
                      fontSize       : '0.75rem',
                      fontWeight     : 700,
                      backgroundColor: e.payment_status === 'paid' ? '#dcfce7' : '#fef9c3',
                      color          : e.payment_status === 'paid' ? '#166534' : '#854d0e',
                    }}>
                      {e.payment_status || '—'}
                    </span>
                  </li>
                  <li><b>{t('stores.expire_at')}:</b> {expiry}</li>
                  {e.payment_status === 'open' && e.cs_checkout_id && (
                    <li style={{ marginTop: '0.5rem' }}>
                      <button
                        style={pill('#3b82f6')}
                        onClick={() => getUrlStore(e.cs_checkout_id!)}
                      >
                        Get URL
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )
        })}
      </>
    )
  }

  // ── Columns ─────────────────────────────────────────────────────────────────
  const columns = [
    {
      name: t('stores.name'),
      cell: (row: Store) => <span className="small text-capitalize">{row.name}</span>,
    },
    {
      name: t('stores.email'),
      cell: (row: Store) => <span className="small">{row.email}</span>,
    },
    {
      name: t('stores.phone'),
      cell: (row: Store) => <span className="small">{row.phone}</span>,
      hide: 'md' as any,
    },
    {
      name: 'Whatsapp',
      cell: (row: Store) => <span className="small">{row.whatsapp}</span>,
      hide: 'md' as any,
    },
    {
      name: t('stores.image'),
      cell: (row: Store) => (
        <img src={`${url}store/${row.image}`} alt={row.name} style={{ width: 64, height: 40, objectFit: 'cover', borderRadius: 6 }} />
      ),
    },
    {
      name    : t('stores.actions'),
      maxWidth: '260px',
      cell    : (row: Store) => (
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', padding: '4px 0' }}>
          <Link to={`edit/${row.id}`} style={pill(BRAND)}>
            <FontAwesomeIcon icon={faPenToSquare} />
            <span>{t('stores.edit')}</span>
          </Link>
          <Link to="create/payment" state={{ store: row.name }} style={pill('#3b82f6')}>
            <FontAwesomeIcon icon={faCirclePlus} />
            <span>{t('stores.create_pay')}</span>
          </Link>
          <button
            onClick={() =>
              row.Storepayments.length > 0
                ? Swal.fire({ icon: 'error', title: 'Error', text: "Can't delete: store has payments" })
                : deleteStore(row.id)
            }
            style={pill(row.Storepayments.length > 0 ? '#94a3b8' : '#ef4444')}
          >
            <FontAwesomeIcon icon={faTrash} />
            <span>{t('stores.delete')}</span>
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="row align-items-start">

      {/* ── Header label ─────────────────────────────────────────────── */}
      <div className="col-sm-4 col-md-2 mb-3 my-1">
        <span className="text-capitalize fw-bold">{t('stores.options')}</span>
        <p className="text-muted small mt-1">Stores &amp; payments</p>
      </div>

      {/* ── Search panel ─────────────────────────────────────────────── */}
      <div className="col-sm-8 col-md-10 mb-3">
        <div style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <button
            type="button"
            onClick={() => setSearchOpen(p => !p)}
            style={{
              width       : '100%',
              display     : 'flex',
              alignItems  : 'center',
              gap         : '0.5rem',
              padding     : '0.75rem 1.25rem',
              background  : searchOpen ? '#f8fafc' : '#fff',
              border      : 'none',
              borderBottom: searchOpen ? '1px solid #e2e8f0' : 'none',
              cursor      : 'pointer',
              fontWeight  : 700,
              fontSize    : '0.9rem',
              color       : '#475569',
              textAlign   : 'left',
            }}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <span>{t('utilities.search')}</span>
            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8', transform: searchOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
          </button>
          {searchOpen && (
            <div style={{ padding: '1.25rem' }}>
              <Formik initialValues={{ search }} onSubmit={handleSearch} enableReinitialize>
                <Form>
                  <div className="row align-items-center mb-3">
                    <label htmlFor="search" className="col-sm-2 col-form-label col-form-label-sm">{t('stores.search')}</label>
                    <div className="col-sm-10">
                      <Field type="text" className="form-control form-control-sm" name="search" id="search" placeholder={t('stores.search')} />
                    </div>
                  </div>
                  <button className="btn btn-sm btn-bd-primary" type="submit">
                    <span className="fw-bold">{t('utilities.search_btn')}</span>
                  </button>
                </Form>
              </Formik>
            </div>
          )}
        </div>
      </div>

      {/* ── Action buttons ───────────────────────────────────────────── */}
      <div className="col-md-12 mb-3" style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
        <Link
          to="create"
          style={{
            display        : 'inline-flex',
            alignItems     : 'center',
            gap            : '0.45rem',
            padding        : '0.5rem 1.25rem',
            borderRadius   : 9999,
            background     : `linear-gradient(135deg, ${BRAND_DARK}, ${BRAND})`,
            color          : '#fff',
            fontWeight     : 700,
            fontSize       : '0.875rem',
            textDecoration : 'none',
            boxShadow      : `0 4px 14px ${BRAND}55`,
          }}
        >
          <FontAwesomeIcon icon={faStore} />
          <span>{t('stores.add_store')}</span>
        </Link>
        <Link
          to="/payments/admin/stores"
          style={{
            display       : 'inline-flex',
            alignItems    : 'center',
            gap           : '0.45rem',
            padding       : '0.5rem 1.25rem',
            borderRadius  : 9999,
            backgroundColor: '#3b82f6',
            color         : '#fff',
            fontWeight    : 700,
            fontSize      : '0.875rem',
            textDecoration: 'none',
            boxShadow     : '0 4px 14px #3b82f655',
          }}
        >
          <FontAwesomeIcon icon={faDollar} />
          <span>{t('stores.payments_store')}</span>
        </Link>
      </div>

      {/* ── DataTable ────────────────────────────────────────────────── */}
      <div className="col-md-12">
        <div className="card card-shadow">
          <DataTable
            columns={columns}
            data={data}
            fixedHeader
            fixedHeaderScrollHeight="600px"
            progressPending={loading}
            paginationResetDefaultPage={resetPaginationToggle}
            pagination
            paginationPerPage={perPage}
            paginationServer
            paginationTotalRows={totalRows}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            paginationComponentOptions={paginationComponentOptions}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            progressComponent={<CustomLoader />}
            dense
            expandableRows
            expandableRowsComponent={ExpandedComponent}
          />
        </div>
      </div>

    </div>
  )
}

export default Stores
